import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApiGet, fetchApiPost } from "./utils";
import { OtcContractLayerDtoValidation, otcContractLayersBaseKey } from "./otc";
import { TradingContract } from "./otc/types";

export const otcAmendmentReviewsBaseKey = "otcAmendmentReviews";

export const OtcAmendmentReviewTaskValidation = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sort_order: z.number(),
  is_completed: z.boolean(),
  completed_at: z.string().nullable(),
  completed_by_user_id: z.string().uuid().nullable(),
  completed_by_user_name: z.string().nullable(),
});

export const OtcAmendmentReviewListItemValidation = z.object({
  contract: z.record(z.unknown()),
  layers: z.array(OtcContractLayerDtoValidation),
  pending_layer_id: z.string().uuid(),
  tasks: z.array(OtcAmendmentReviewTaskValidation),
  completed_task_count: z.number(),
  total_task_count: z.number(),
  all_tasks_completed: z.boolean(),
});

export type OtcAmendmentReviewListItem = Omit<
  z.infer<typeof OtcAmendmentReviewListItemValidation>,
  "contract"
> & {
  contract: TradingContract;
};

export const SetOtcAmendmentReviewTaskCompletionRequestValidation = z.object({
  layer_id: z.string().uuid(),
  task_instance_id: z.string().uuid(),
  is_completed: z.boolean(),
});

export const MarkOtcAmendmentLayerReviewedRequestValidation = z.object({
  layer_id: z.string().uuid(),
});

export type OtcAmendmentReviewTask = z.infer<typeof OtcAmendmentReviewTaskValidation>;

export interface SetOtcAmendmentReviewTaskCompletionResponse {
  task_instance_id: string;
  is_completed: boolean;
  completed_at: string | null;
  completed_by_user_id: string | null;
}

export interface MarkOtcAmendmentLayerReviewedResponse {
  layer_id: string;
  trading_contract_id: string;
  reviewed_at: string;
  reviewed_by_user_id: string;
}

export function useListOtcAmendmentReviews() {
  return useQuery({
    queryKey: [otcAmendmentReviewsBaseKey],
    queryFn: () =>
      fetchApiGet<OtcAmendmentReviewListItem[]>("otc/amendment-reviews"),
    retry: 1,
  });
}

export function useSetOtcAmendmentReviewTaskCompletion() {
  const queryClient = useQueryClient();

  return async (
    params: z.infer<typeof SetOtcAmendmentReviewTaskCompletionRequestValidation>,
    contract_id?: string,
  ) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<SetOtcAmendmentReviewTaskCompletionResponse>(
      "otc/amendment-reviews/set-task-completion",
      params,
      queryParams,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [otcAmendmentReviewsBaseKey] });
      if (contract_id) {
        queryClient.invalidateQueries({
          queryKey: [otcContractLayersBaseKey, contract_id],
        });
      }
    }

    return result;
  };
}

export function useMarkOtcAmendmentLayerReviewed() {
  const queryClient = useQueryClient();

  return async (
    params: z.infer<typeof MarkOtcAmendmentLayerReviewedRequestValidation>,
    contract_id?: string,
  ) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<MarkOtcAmendmentLayerReviewedResponse>(
      "otc/amendment-reviews/mark-reviewed",
      params,
      queryParams,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [otcAmendmentReviewsBaseKey] });
      if (contract_id) {
        queryClient.invalidateQueries({
          queryKey: [otcContractLayersBaseKey, contract_id],
        });
      }
    }

    return result;
  };
}
